import { IMetadataTab } from "models/metadata";
import React, { useMemo, useState } from "react";
import MetadataContainer from "./MetadataContainer";
import { Accordion, AccordionDetails, AccordionSummary, CircularProgress, Typography } from "@mui/material";
import MetadataNotFound from "./MetadataNotFound";
import MetadataForm from "./MetadataForm";
import { GridExpandMoreIcon } from "@mui/x-data-grid-pro";

import { OnGetMetadataSignature, OnSetMetadataSignature } from './MetadataEventTypes';
import { GuidOrNumber } from "models/common";

type MetadataPanelProps = {
    loading: boolean;
    activeTab: IMetadataTab | null;    
    onLoadValues: OnGetMetadataSignature;
    onUpdateValue: OnSetMetadataSignature;
};

const MetadataPanel: React.FC<MetadataPanelProps> = (props) => {
    const {
        loading,
        activeTab,
        onLoadValues,
        onUpdateValue
    } = props;

    const [ expanded, setExpanded ] = useState<GuidOrNumber | false>(false);

    const handleExpandChange = (groupId: GuidOrNumber | undefined) => (event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? (groupId ?? false) : false);
    };

    const hasGroups = useMemo(() => activeTab && activeTab.groups && activeTab?.groups.length, [
        activeTab
    ]);
    const hasComponents = useMemo(() => activeTab && activeTab.components && activeTab.components.length > 0, [
        activeTab
    ]);

    if (activeTab === null)
    {
        return (            
            <MetadataNotFound />
        );
    }

    /// Render groups in accordion for master tabs
    if (activeTab.isMasterTab) {
        if (!hasGroups && !hasComponents) {
            return (            
                <MetadataNotFound />
            );
        }

        return (
            <MetadataContainer>
                <CircularProgress hidden={!loading} />
                { 
                    hasComponents ?
                        <MetadataForm
                            loading={loading} 
                            metadataId={activeTab?.id ?? 0}
                            components={activeTab.components!}
                            onLoadValues={onLoadValues}
                            onUpdateValue={onUpdateValue}
                        />
                    : null }
                    { hasGroups && activeTab.groups?.map((group, index) => (
                        <Accordion
                            expanded={group.id === expanded}
                            onChange={handleExpandChange(group.id)}
                        >
                            <AccordionSummary
                                expandIcon={<GridExpandMoreIcon />}                            
                            >
                                <Typography>
                                    {group.name}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <MetadataForm 
                                    loading={loading} 
                                    metadataId={group.id ?? 0}
                                    components={group.components}
                                    onLoadValues={onLoadValues}
                                    onUpdateValue={onUpdateValue}
                                />
                            </AccordionDetails>
                        </Accordion>
                    ))
                }
            </MetadataContainer>
        );
    } else {
        /// Non master tab metadata renders form directly.
        if (!hasComponents) {
            return (            
                <MetadataNotFound />
            );
        }

        return (
            <MetadataContainer>
                <MetadataForm
                    loading={loading} 
                    metadataId={activeTab?.id ?? 0}
                    components={activeTab.components!}
                    onLoadValues={onLoadValues}
                    onUpdateValue={onUpdateValue}
                />                
            </MetadataContainer>
        );
    }
};

export default MetadataPanel;